import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../styles/Navbar.scss';

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProjectDropdownOpen, setIsProjectDropdownOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState('MBA');

  const location = useLocation();
  const navigate = useNavigate();
  const navbarRef = useRef(null);

  const aboutTriggerRef = useRef(null);
  const aboutMenuRef = useRef(null);
  const projectTriggerRef = useRef(null);
  const projectMenuRef = useRef(null);

  const isActive = (path) => location.pathname === path ? 'active' : '';

  const domains = ['MBA', 'Software Project'];
  const getDomainLink = (domain) => (domain === 'MBA' ? '/mba-projects' : '/program-projects?program=Software-Project');

  // Close menu when route or hash changes
  useEffect(() => {
    setIsOpen(false);
    setIsProjectDropdownOpen(false);
    setIsAboutDropdownOpen(false);
  }, [location.pathname, location.hash]);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsProjectDropdownOpen(false);
        setIsAboutDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle scroll event
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  const handleAboutKeyDown = (e) => {
    const focusableElements = aboutMenuRef.current ? aboutMenuRef.current.querySelectorAll('a') : [];
    const activeElement = document.activeElement;
    
    if (e.key === 'Escape') {
      setIsAboutDropdownOpen(false);
      aboutTriggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isAboutDropdownOpen) {
        setIsAboutDropdownOpen(true);
      } else if (focusableElements.length > 0) {
        let nextIndex = 0;
        const currentIndex = Array.from(focusableElements).indexOf(activeElement);
        if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
          nextIndex = currentIndex + 1;
        }
        focusableElements[nextIndex]?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isAboutDropdownOpen && focusableElements.length > 0) {
        let prevIndex = focusableElements.length - 1;
        const currentIndex = Array.from(focusableElements).indexOf(activeElement);
        if (currentIndex > 0) {
          prevIndex = currentIndex - 1;
        }
        focusableElements[prevIndex]?.focus();
      }
    }
  };

  const handleProjectKeyDown = (e) => {
    const focusableElements = projectMenuRef.current ? projectMenuRef.current.querySelectorAll('a') : [];
    const activeElement = document.activeElement;
    
    if (e.key === 'Escape') {
      setIsProjectDropdownOpen(false);
      projectTriggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!isProjectDropdownOpen) {
        setIsProjectDropdownOpen(true);
      } else if (focusableElements.length > 0) {
        let nextIndex = 0;
        const currentIndex = Array.from(focusableElements).indexOf(activeElement);
        if (currentIndex !== -1 && currentIndex < focusableElements.length - 1) {
          nextIndex = currentIndex + 1;
        }
        focusableElements[nextIndex]?.focus();
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (isProjectDropdownOpen && focusableElements.length > 0) {
        let prevIndex = focusableElements.length - 1;
        const currentIndex = Array.from(focusableElements).indexOf(activeElement);
        if (currentIndex > 0) {
          prevIndex = currentIndex - 1;
        }
        focusableElements[prevIndex]?.focus();
      }
    }
  };

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''}`} ref={navbarRef}>
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img src="/images/logo.png" alt="Pravara InfoTech" className="navbar-logo-img" />
          <span className="logo-text">Pravara InfoTech</span>
        </Link>
        {/* Overlay for mobile menu */}
        <div className={`navbar-overlay ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(false)}></div>

        <ul className={`navbar-menu ${isOpen ? 'active' : ''}`}>
          <li className="mobile-menu-header">
            <span className="mobile-logo-text">MENU</span>
          </li>
          <li className="navbar-item"><Link to="/" className={`nav-link ${isActive('/')}`} onClick={() => setIsOpen(false)}>Home</Link></li>
          
          {/* ABOUT Dropdown Link */}
          <li
            className="navbar-item navbar-dropdown"
            onMouseEnter={() => { if (window.innerWidth > 1024) setIsAboutDropdownOpen(true); }}
            onMouseLeave={() => { if (window.innerWidth > 1024) setIsAboutDropdownOpen(false); }}
            onKeyDown={handleAboutKeyDown}
          >
            <button
              ref={aboutTriggerRef}
              className={`nav-link ${location.pathname.startsWith('/about') ? 'active' : ''} dropdown-toggle ${isAboutDropdownOpen ? 'open' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                if (window.innerWidth <= 1024) {
                  setIsAboutDropdownOpen(!isAboutDropdownOpen);
                } else {
                  navigate('/about');
                }
              }}
              aria-expanded={isAboutDropdownOpen}
              aria-haspopup="true"
            >
              About <span className="arrow">▾</span>
            </button>
            <div 
              ref={aboutMenuRef}
              className={`dropdown-menu ${isAboutDropdownOpen ? 'active' : ''}`}
            >
              <Link
                to="/about/story"
                className={`dropdown-item ${location.pathname === '/about/story' ? 'selected' : ''}`}
                onClick={() => {
                  setIsAboutDropdownOpen(false);
                  setIsOpen(false);
                }}
              >
                Our Story
              </Link>
              <Link
                to="/about/vision-mission"
                className={`dropdown-item ${location.pathname === '/about/vision-mission' ? 'selected' : ''}`}
                onClick={() => {
                  setIsAboutDropdownOpen(false);
                  setIsOpen(false);
                }}
              >
                Our Vision & Mission
              </Link>
              <Link
                to="/about?openGallery=true"
                className={`dropdown-item ${location.search.includes('openGallery=true') ? 'selected' : ''}`}
                onClick={() => {
                  setIsAboutDropdownOpen(false);
                  setIsOpen(false);
                }}
              >
                Gallery
              </Link>
              <Link
                to="/about/our-team"
                className={`dropdown-item ${location.pathname === '/about/our-team' ? 'selected' : ''}`}
                onClick={() => {
                  setIsAboutDropdownOpen(false);
                  setIsOpen(false);
                }}
              >
                Our Team
              </Link>
              <Link
                to="/collaborations"
                className={`dropdown-item ${location.pathname === '/collaborations' ? 'selected' : ''}`}
                onClick={() => {
                  setIsAboutDropdownOpen(false);
                  setIsOpen(false);
                }}
              >
                Collaboration
              </Link>
            </div>
          </li>

          <li className="navbar-item"><Link to="/services" className={`nav-link ${isActive('/services')}`} onClick={() => setIsOpen(false)}>Services</Link></li>

          {/* PROJECTS Dropdown Link */}
          <li
            className="navbar-item navbar-dropdown"
            onMouseEnter={() => { if (window.innerWidth > 1024) setIsProjectDropdownOpen(true); }}
            onMouseLeave={() => { if (window.innerWidth > 1024) setIsProjectDropdownOpen(false); }}
            onKeyDown={handleProjectKeyDown}
          >
            <button
              ref={projectTriggerRef}
              className={`nav-link ${isActive('/projects')} dropdown-toggle ${isProjectDropdownOpen ? 'open' : ''}`}
              onClick={(e) => {
                e.preventDefault();
                if (window.innerWidth <= 1024) {
                  setIsProjectDropdownOpen(!isProjectDropdownOpen);
                }
              }}
              aria-expanded={isProjectDropdownOpen}
              aria-haspopup="true"
            >
              Projects <span className="arrow">▾</span>
            </button>
            <div 
              ref={projectMenuRef}
              className={`dropdown-menu ${isProjectDropdownOpen ? 'active' : ''}`}
            >
              {domains.map(domain => (
                <Link
                  key={domain}
                  to={getDomainLink(domain)}
                  className={`dropdown-item ${selectedDomain === domain ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedDomain(domain);
                    setIsProjectDropdownOpen(false);
                    setIsOpen(false);
                  }}
                >
                  {domain}
                </Link>
              ))}
            </div>
          </li>

          <li className="navbar-item"><Link to="/contact" className={`nav-link ${isActive('/contact')}`} onClick={() => setIsOpen(false)}>Contact</Link></li>
          <li className="navbar-item"><Link to="/career" className={`nav-link ${isActive('/career')}`} onClick={() => setIsOpen(false)}>Career</Link></li>
        </ul>
        <div className={`hamburger ${isOpen ? 'active' : ''}`} onClick={() => setIsOpen(!isOpen)}>
          <span></span><span></span><span></span>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
